"use client";

import { useRef, useEffect } from "react";
import { ImageUp, X, Lightbulb } from "lucide-react";

import { cn } from "@/lib/utils";
import { AlertDialogType } from "@/types/alert";
import { MODELS } from "../constants";
import { useAppSelector, useAppDispatch } from "@/redux/store";
import { addAlertDialog } from "@/redux/actions";

const MAX_FILE_SIZE = 10;
const MAX_IMAGES = 9;

interface PromptInputAreaProps {
  prompt: string;
  onChange: (value: string) => void;
  onCreate: () => Promise<void>;
  images: File[];
  setImages: (files: File[]) => void;
  isGenerating: boolean;
  selectedModel: string;
}

const PromptInputArea: React.FC<PromptInputAreaProps> = ({
  prompt,
  onChange,
  onCreate,
  images,
  setImages,
  isGenerating,
  selectedModel,
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const model = MODELS.find((m) => m.name === selectedModel) || MODELS[0];

  const handlePromptKeyPress = (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onCreate();
    }
  };

  const handleClearPrompt = () => {
    onChange("");
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleImageInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (!isSubscribed) {
      e.preventDefault();
      dispatch(
        addAlertDialog({
          title: "Subscription Required",
          description: "Upgrade to a premium plan to upload images!",
          nextButtonText: "Upgrade",
          type: AlertDialogType.SubscriptionRequired,
        })
      );
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      if (files.length + images.length > MAX_IMAGES) {
        alert(`You can only select up to ${MAX_IMAGES} images.`);
        return;
      }

      const validFiles = files.filter(
        (file) => file.size <= MAX_FILE_SIZE * 1024 * 1024
      );
      if (validFiles.length !== files.length) {
        alert(`Some files exceed the maximum size of ${MAX_FILE_SIZE} MB.`);
        return;
      }

      setImages([...images, ...files]);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div className="tw:flex tw:flex-col tw:gap-2.5">
      <div className="tw:flex tw:items-center tw:gap-1.5">
        <Lightbulb size={12} className="tw:text-[#838383]" />
        <p className="tw:text-xxs">Your Idea</p>
      </div>
      <div className="tw:relative tw:flex tw:w-full tw:rounded-lg tw:border tw:border-primary-red tw:pt-3 tw:pb-1 tw:pl-4 tw:pr-1">
        <div className="tw:w-full">
          <textarea
            value={Boolean(isGenerating && prompt) ? `"${prompt}"` : prompt}
            onChange={(e) => onChange(e.target.value)}
            placeholder={
              isGenerating
                ? "Generating..."
                : "Describe the image you want to create, in any language"
            }
            className={cn(
              "tw:w-full tw:border-none tw:outline-none tw:text-base tw:placeholder:text-neutral-500 tw:h-[158px] tw:resize-none tw:disabled:opacity-50",
              images.length > 0 && "tw:h-[110px]"
            )}
            onKeyDown={handlePromptKeyPress}
            ref={inputRef}
            disabled={isGenerating}
          />
          {images.length > 0 && (
            <div className="tw:flex tw:flex-nowrap tw:items-center tw:gap-1 tw:w-full tw:h-12 tw:overflow-x-auto tw:pr-4">
              {images.map((image, index) => (
                <div
                  key={`image-preview-${index + 1}`}
                  className="tw:inline-flex tw:flex-none tw:relative tw:rounded-md tw:overflow-visible"
                >
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Upload ${index + 1}`}
                    className="tw:w-10 tw:h-10 tw:object-cover tw:rounded-md tw:border tw:border-gray-300"
                  />
                  <button
                    className="tw:absolute tw:-top-1 tw:-right-1 tw:w-4 tw:h-4 tw:grid tw:place-items-center tw:text-gray-500 tw:bg-white tw:rounded-full tw:shadow-[0_2px_4px_rgba(0,0,0,0.2)] tw:disabled:opacity-30"
                    onClick={() => handleRemoveImage(index)}
                    disabled={isGenerating}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="tw:relative tw:flex tw:flex-col tw:justify-end tw:items-center">
          <button
            aria-label="Upload Image(s)"
            className="tw:grid tw:place-items-center tw:bg-primary-red tw:rounded-full tw:w-7 tw:h-7 tw:text-white tw:disabled:opacity-30"
            disabled={
              !model.imageEdits ||
              images.length >= MAX_IMAGES ||
              isGenerating ||
              !isSubscribed
            }
          >
            <ImageUp size={16} />
          </button>
          <input
            type="file"
            className="tw:absolute tw:bottom-0 tw:left-0 tw:h-7 tw:w-full tw:cursor-pointer tw:opacity-0"
            id="imageUpload"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            multiple
            onChange={handleImageUpload}
            onClick={handleImageInputClick}
            disabled={
              !model.imageEdits || images.length >= MAX_IMAGES || isGenerating
            }
          />
        </div>
        {prompt && !isGenerating && (
          <button
            aria-label="Clear Prompt"
            className="tw:absolute tw:right-2 tw:top-[-12px] tw:bg-gray-100 tw:text-xs tw:py-1 tw:px-3 tw:rounded-xl tw:border-none"
            onClick={handleClearPrompt}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default PromptInputArea;
